import faceSaraChen from "./assets/face-sara-chen.png";
import faceHeartInstitute from "./assets/face-heart-institute.png";
import logoSleepDeepDive from "./assets/logo-sleep-deep-dive.png";

const SKILLS = {
  sleep: { primary: "#4A90D9", accent: "#E8A23C" },
  nutrition: { primary: "#E8853A", accent: "#3A7EBF" },
  cardio: { primary: "#D9536A", accent: "#3E8C72" },
  fitness: { primary: "#7B61FF", accent: "#D4A843" },
  mental: { primary: "#3AADA8", accent: "#A86ED6" },
  longevity: { primary: "#9B6B3D", accent: "#5A8CBF" },
  recovery: { primary: "#5B8C5A", accent: "#C07A4A" },
};

// Shorthand for templates
const skillColors = Object.fromEntries(Object.entries(SKILLS).map(([k, v]) => [k, v.primary]));

const KIN_GREEN = "#2FB56A";

function mix(color, percent) {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

function BrowserDots() {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {["#FF6B6B", "#F0B800", "#2FB56A"].map((color) => (
        <span
          key={color}
          style={{ width: 8, height: 8, borderRadius: "50%", background: color, opacity: 0.85 }}
        />
      ))}
    </div>
  );
}

function SidebarItem({ label, active = false }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 12,
        background: active ? "rgba(0,0,0,0.05)" : "transparent",
        border: active ? "1px solid rgba(0,0,0,0.06)" : "1px solid transparent",
        color: active ? "var(--color-kin-black)" : "var(--color-kin-muted)",
        fontSize: 11,
        fontWeight: active ? 700 : 600,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </div>
  );
}

/* ─── Completed analysis card ─────────────────────────────────────────────── */

function CompletedAnalysis({
  skill,
  creator,
  face,
  logo,
  color,
  accent,
  time,
  summary,
  dataSource,
  metrics,
}) {
  return (
    <div
      style={{
        borderRadius: 18,
        overflow: "hidden",
        background: mix(color, 8),
        border: `1px solid ${mix(color, 16)}`,
      }}
    >
      {/* Color strip */}
      <div style={{ display: "flex", height: 4 }}>
        <div style={{ flex: 3, background: color }} />
        <div style={{ flex: 1, background: accent || "rgba(255,255,255,0.5)" }} />
        <div style={{ flex: 4, background: mix(color, 18) }} />
      </div>

      <div style={{ padding: "14px 16px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {logo ? (
            <img
              src={logo}
              alt=""
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "#fff",
                padding: 3,
                border: `1px solid ${mix(color, 20)}`,
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: mix(color, 14),
                border: `1px solid ${mix(color, 24)}`,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 700,
                color,
                fontFamily: "'Cormorant Garamond', serif",
              }}
            >
              {skill.charAt(0)}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "var(--color-kin-black)",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  lineHeight: 1.1,
                }}
              >
                {skill}
              </p>
              <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>{time}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
              {face ? (
                <img
                  src={face}
                  alt=""
                  style={{ width: 12, height: 12, borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: mix(color, 22),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 7,
                    fontWeight: 700,
                    color,
                  }}
                >
                  {creator.charAt(0)}
                </div>
              )}
              <span style={{ fontSize: 9, fontWeight: 600, color }}>{creator}</span>
            </div>
          </div>
        </div>

        {/* Summary */}
        <p
          style={{ marginTop: 10, fontSize: 11.5, color: "var(--color-kin-body)", lineHeight: 1.5 }}
        >
          {summary}
        </p>

        {/* Metrics */}
        {metrics && (
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            {metrics.map((m) => (
              <div
                key={m.label}
                style={{
                  borderRadius: 10,
                  padding: "6px 10px",
                  background: "rgba(255,255,255,0.6)",
                  border: `1px solid ${mix(color, 12)}`,
                }}
              >
                <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>{m.label} </span>
                <span style={{ fontSize: 10, fontWeight: 700, color }}>{m.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Provenance */}
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: KIN_GREEN,
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>{dataSource}</span>
        </div>

        {/* Action */}
        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <div
            style={{
              padding: "7px 14px",
              borderRadius: 10,
              background: KIN_GREEN,
              fontSize: 10,
              fontWeight: 700,
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Open skill
          </div>
          <div
            style={{
              padding: "7px 14px",
              borderRadius: 10,
              background: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(0,0,0,0.06)",
              fontSize: 10,
              fontWeight: 600,
              color: "var(--color-kin-body)",
              cursor: "pointer",
            }}
          >
            History
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Active skill card ───────────────────────────────────────────────────── */

function ActiveSkillCard({ skill, creator, face, logo, color, accent, status, lastRun }) {
  return (
    <div
      style={{
        borderRadius: 16,
        overflow: "hidden",
        background: mix(color, 6),
        border: `1px solid ${mix(color, 14)}`,
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", height: 3 }}>
        <div style={{ flex: 3, background: color }} />
        <div style={{ flex: 1, background: accent || "rgba(255,255,255,0.5)" }} />
        <div style={{ flex: 4, background: mix(color, 16) }} />
      </div>
      <div style={{ padding: "12px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {logo ? (
            <img
              src={logo}
              alt=""
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                background: "#fff",
                padding: 3,
                border: `1px solid ${mix(color, 18)}`,
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                background: mix(color, 12),
                border: `1px solid ${mix(color, 20)}`,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color,
                fontFamily: "'Cormorant Garamond', serif",
              }}
            >
              {skill.charAt(0)}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--color-kin-black)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                lineHeight: 1.15,
              }}
            >
              {skill}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 2 }}>
              {face ? (
                <img
                  src={face}
                  alt=""
                  style={{ width: 10, height: 10, borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: mix(color, 20),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 6,
                    fontWeight: 700,
                    color,
                  }}
                >
                  {creator.charAt(0)}
                </div>
              )}
              <span style={{ fontSize: 8, fontWeight: 600, color }}>{creator}</span>
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>{lastRun}</span>
          <span
            style={{
              fontSize: 9,
              fontWeight: 600,
              color: status === "Ready" ? KIN_GREEN : "var(--color-kin-gold)",
            }}
          >
            {status}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Discover skill card ─────────────────────────────────────────────────── */

function DiscoverSkillCard({ skill, creator, color, accent, description, users }) {
  return (
    <div
      style={{
        borderRadius: 16,
        overflow: "hidden",
        background: "rgba(255,255,255,0.6)",
        border: "1px solid rgba(0,0,0,0.06)",
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", height: 3 }}>
        <div style={{ flex: 3, background: color }} />
        <div style={{ flex: 1, background: accent || "rgba(255,255,255,0.5)" }} />
        <div style={{ flex: 4, background: mix(color, 16) }} />
      </div>
      <div style={{ padding: "12px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: mix(color, 12),
              border: `1px solid ${mix(color, 20)}`,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              color,
              fontFamily: "'Cormorant Garamond', serif",
            }}
          >
            {skill.charAt(0)}
          </div>
          <div style={{ flex: 1 }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--color-kin-black)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
              }}
            >
              {skill}
            </p>
            <span style={{ fontSize: 8, fontWeight: 600, color }}>{creator}</span>
          </div>
        </div>
        <p style={{ marginTop: 8, fontSize: 10, color: "var(--color-kin-body)", lineHeight: 1.45 }}>
          {description}
        </p>
        <div
          style={{
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>{users} users</span>
          <div
            style={{
              padding: "5px 12px",
              borderRadius: 8,
              background: KIN_GREEN,
              fontSize: 9,
              fontWeight: 700,
              color: "#fff",
            }}
          >
            Add skill
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main screen ─────────────────────────────────────────────────────────── */

export function SkillsWorkbenchScreen() {
  return (
    <article
      style={{
        width: "min(100%, 1180px)",
        borderRadius: 30,
        background:
          "linear-gradient(180deg, color-mix(in srgb, var(--color-kin-white) 88%, transparent) 0%, color-mix(in srgb, var(--color-kin-white) 74%, transparent) 100%)",
        border: "1px solid color-mix(in srgb, var(--color-kin-white-pure) 46%, transparent)",
        boxShadow:
          "0 28px 44px -28px rgba(18,22,19,0.24), 0 10px 18px -12px rgba(18,22,19,0.14), inset 0 1px 0 rgba(255,255,255,0.92)",
        overflow: "hidden",
        backdropFilter: "blur(24px) saturate(135%)",
      }}
    >
      {/* Browser chrome */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 18px",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          background: "color-mix(in srgb, var(--color-kin-white) 80%, transparent)",
        }}
      >
        <BrowserDots />
        <div
          style={{
            width: 260,
            borderRadius: 999,
            background: "rgba(0,0,0,0.04)",
            color: "var(--color-kin-muted)",
            fontSize: 10.5,
            fontWeight: 600,
            textAlign: "center",
            padding: "6px 12px",
          }}
        >
          kin.health / skills
        </div>
        <div style={{ width: 68 }} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "176px minmax(0, 1fr)",
          gap: 18,
          padding: 18,
        }}
      >
        {/* Sidebar */}
        <aside
          style={{
            borderRadius: 22,
            background: "color-mix(in srgb, var(--color-kin-sage) 92%, transparent)",
            border: "1px solid rgba(0,0,0,0.05)",
            padding: 14,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ marginBottom: 10 }}>
            <p
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "var(--color-kin-black)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
              }}
            >
              Kin
            </p>
          </div>
          <SidebarItem label="Home" />
          <SidebarItem label="Skills" active />
          {/* Expanded skill list */}
          <div
            style={{
              marginLeft: 8,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              marginTop: -4,
              marginBottom: 4,
            }}
          >
            {[
              { name: "Sleep Deep Dive", color: skillColors.sleep },
              { name: "Metabolic Load", color: skillColors.nutrition },
              { name: "Cardio Baseline", color: skillColors.cardio },
              { name: "Fitness Age", color: skillColors.fitness },
              { name: "Stress Index", color: skillColors.mental },
              { name: "Recovery Score", color: "#5B8C5A" },
              { name: "Longevity Markers", color: skillColors.longevity },
            ].map((s) => (
              <div
                key={s.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 8px",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: s.color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 10, color: "var(--color-kin-muted)", fontWeight: 500 }}>
                  {s.name}
                </span>
              </div>
            ))}
          </div>
          <SidebarItem label="Health Data" />
          <SidebarItem label="Vault" />
          <SidebarItem label="Briefs" />
          <SidebarItem label="Settings" />

          <div
            style={{
              marginTop: "auto",
              padding: "12px 12px 13px",
              borderRadius: 16,
              background: "rgba(0,0,0,0.04)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "var(--color-kin-green-soft)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              S
            </div>
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: "var(--color-kin-black)" }}>
                Sverre
              </p>
              <p style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>7 active skills</p>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 18,
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--color-kin-muted)",
                }}
              >
                Skills
              </p>
              <h2
                style={{
                  marginTop: 6,
                  fontSize: 40,
                  fontWeight: 700,
                  color: "var(--color-kin-black)",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  lineHeight: 0.98,
                }}
              >
                Your skills
              </h2>
            </div>
            {/* Search */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                borderRadius: 12,
                background: "rgba(0,0,0,0.04)",
                border: "1px solid rgba(0,0,0,0.06)",
                width: 240,
              }}
            >
              <span style={{ fontSize: 12, color: "var(--color-kin-muted)" }}>⌕</span>
              <span style={{ fontSize: 11, color: "var(--color-kin-muted)" }}>Search skills…</span>
            </div>
          </div>

          {/* ── Latest completed analyses ── */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--color-kin-muted)",
                }}
              >
                Latest analyses
              </p>
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-kin-green-dark)" }}>
                See all →
              </span>
            </div>
            <div
              style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}
            >
              <CompletedAnalysis
                skill="Sleep Deep Dive"
                creator="Dr. Sara Chen"
                face={faceSaraChen}
                logo={logoSleepDeepDive}
                color={SKILLS.sleep.primary}
                accent={SKILLS.sleep.accent}
                time="24m ago"
                summary="Deep sleep increased 22% this week. Recovery improved after earlier dinners and lower-intensity training."
                dataSource="Based on 14 sleep stage records from Oura + 2 meal logs"
                metrics={[
                  { label: "Score", value: "84" },
                  { label: "Deep", value: "1h 42m" },
                  { label: "Recovery", value: "High" },
                ]}
              />
              <CompletedAnalysis
                skill="Cardio Baseline"
                creator="Heart Institute"
                face={faceHeartInstitute}
                logo={null}
                color={SKILLS.cardio.primary}
                accent={SKILLS.cardio.accent}
                time="2h ago"
                summary="Resting heart rate stable at 58 bpm. CRP within range — no cardiovascular inflammation signal."
                dataSource="Reacted to CRP and lipid panel from lab results"
                metrics={[
                  { label: "HR", value: "58 bpm" },
                  { label: "CRP", value: "Normal" },
                ]}
              />
            </div>
          </div>

          {/* ── Your active skills ── */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--color-kin-muted)",
                }}
              >
                Active skills
              </p>
              <span style={{ fontSize: 10, color: "var(--color-kin-muted)" }}>7 skills</span>
            </div>
            <div
              style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }}
            >
              <ActiveSkillCard
                skill="Sleep Deep Dive"
                creator="Dr. Sara Chen"
                face={faceSaraChen}
                logo={logoSleepDeepDive}
                color={SKILLS.sleep.primary}
                accent={SKILLS.sleep.accent}
                status="Ready"
                lastRun="24m ago"
              />
              <ActiveSkillCard
                skill="Metabolic Load"
                creator="NutriLab"
                face={null}
                logo={null}
                color={SKILLS.nutrition.primary}
                accent={SKILLS.nutrition.accent}
                status="Needs data"
                lastRun="Yesterday"
              />
              <ActiveSkillCard
                skill="Cardio Baseline"
                creator="Heart Institute"
                face={faceHeartInstitute}
                logo={null}
                color={SKILLS.cardio.primary}
                accent={SKILLS.cardio.accent}
                status="Ready"
                lastRun="2h ago"
              />
              <ActiveSkillCard
                skill="Fitness Age"
                creator="MoveWell"
                face={null}
                logo={null}
                color={SKILLS.fitness.primary}
                accent={SKILLS.fitness.accent}
                status="Ready"
                lastRun="3 days ago"
              />
              <ActiveSkillCard
                skill="Stress Index"
                creator="MindLab"
                face={null}
                logo={null}
                color={SKILLS.mental.primary}
                accent={SKILLS.mental.accent}
                status="Ready"
                lastRun="1 day ago"
              />
              <ActiveSkillCard
                skill="Recovery Score"
                creator="Dr. Sara Chen"
                face={faceSaraChen}
                logo={null}
                color={SKILLS.recovery.primary}
                accent={SKILLS.recovery.accent}
                status="Ready"
                lastRun="24m ago"
              />
              <ActiveSkillCard
                skill="Longevity Markers"
                creator="AgeWell Research"
                face={null}
                logo={null}
                color={SKILLS.longevity.primary}
                accent={SKILLS.longevity.accent}
                status="Needs data"
                lastRun="5 days ago"
              />
            </div>
          </div>

          {/* ── Find skills ── */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--color-kin-muted)",
                  }}
                >
                  Discover
                </p>
                <p
                  style={{
                    marginTop: 4,
                    fontSize: 20,
                    fontWeight: 700,
                    color: "var(--color-kin-black)",
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                  }}
                >
                  Find new skills
                </p>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-kin-green-dark)" }}>
                Browse marketplace →
              </span>
            </div>

            {/* Category filters */}
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {["All", "Recovery", "Nutrition", "Cardiovascular", "Fitness", "Mental health"].map(
                (cat, i) => (
                  <span
                    key={cat}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 8,
                      background: i === 0 ? "var(--color-kin-black)" : "rgba(0,0,0,0.04)",
                      border: i === 0 ? "none" : "1px solid rgba(0,0,0,0.06)",
                      fontSize: 10,
                      fontWeight: 600,
                      color: i === 0 ? "#fff" : "var(--color-kin-muted)",
                      cursor: "pointer",
                    }}
                  >
                    {cat}
                  </span>
                ),
              )}
            </div>

            <div
              style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}
            >
              <DiscoverSkillCard
                skill="Gut Health Score"
                creator="BioFlora Lab"
                color="#6AAF6D"
                accent="#B85C8A"
                description="Analyse microbiome test results, food logs, and digestive symptom tracking to build a personalised gut health profile."
                users="1,200"
              />
              <DiscoverSkillCard
                skill="Hormone Cycles"
                creator="EndoHealth"
                color="#C06CAA"
                accent="#5AAF82"
                description="Track hormonal fluctuations across sleep, mood, and energy data. Designed for anyone with cyclical hormone patterns."
                users="890"
              />
              <DiscoverSkillCard
                skill="Inflammation Tracker"
                creator="ImmunoLab"
                color="#D18C47"
                accent="#4A7EBF"
                description="Monitor inflammatory biomarkers over time. Cross-references diet, exercise intensity, and sleep quality to find triggers."
                users="650"
              />
            </div>
          </div>
        </main>
      </div>
    </article>
  );
}

/* ─── Page shell ──────────────────────────────────────────────────────────── */

export default function WebappSkillsPage() {
  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(ellipse at 15% 10%, rgba(255,208,40,0.06) 0%, transparent 50%), radial-gradient(ellipse at 85% 90%, rgba(91,122,102,0.05) 0%, transparent 50%), linear-gradient(168deg, #F8F5EE 0%, #F5F3EC 35%, #F0F2EE 65%, #EEF2EB 100%)",
        fontFamily: "'Manrope', system-ui",
      }}
    >
      <nav
        className="sticky top-0 z-40 flex items-center gap-4 px-6 py-3 sm:px-10"
        style={{
          background: "rgba(247,245,238,0.72)",
          backdropFilter: "blur(20px) saturate(120%)",
          borderBottom: "1px solid rgba(255,255,255,0.64)",
        }}
      >
        <a
          href="/private/design"
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            textDecoration: "none",
          }}
        >
          ← Brandbook
        </a>
        <span style={{ color: "var(--color-kin-olive-light)" }}>·</span>
        <a
          href="/private/design/webapp"
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            textDecoration: "none",
          }}
        >
          Webapp Home
        </a>
        <span style={{ color: "var(--color-kin-olive-light)" }}>·</span>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--color-kin-black)",
          }}
        >
          Skills
        </p>
      </nav>

      <header className="max-w-5xl px-6 pb-10 pt-14 sm:px-10 lg:px-16">
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            marginBottom: 14,
          }}
        >
          Webapp
        </p>
        <h1
          style={{
            fontSize: "clamp(40px, 6vw, 68px)",
            fontWeight: 700,
            color: "var(--color-kin-black)",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            lineHeight: 1.0,
            letterSpacing: "-0.01em",
            marginBottom: 20,
          }}
        >
          Skills
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "var(--color-kin-muted)",
            lineHeight: 1.7,
            maxWidth: 760,
            fontWeight: 400,
          }}
        >
          Your active skills, their latest analyses, and new skills to discover. Each skill reacts
          to your health data independently — you control which skills have access.
        </p>
      </header>

      <section className="px-6 pb-16 sm:px-10 lg:px-16">
        <SkillsWorkbenchScreen />
      </section>
    </div>
  );
}
